import {
  clearAllObservationsFromLocalStorage,
  generateCodeChallenge,
  generateCodeVerifier,
  generateNonce,
  isTokenExpired,
  urlParamsToJson,
} from '@/lib/utils';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import Cookies from 'universal-cookie';
import { mixPanelEvents } from '@/analytics/mixpanel/events';
import useUserInfo, { UserInfo } from './useUserInfo';
import { mixPanelServiceInstance } from '@/analytics/mixpanel';
import storageService from '@/lib/storage-service';
import {
  ACCESS_TOKEN,
  CACHE_KEY_PERMISSIONS,
  CACHE_KEY_USER_INFO,
} from '@/constants/storage.constants';

type AuthContextType = {
  user: UserInfo | null;
  loadingUserInfo: boolean;
  error: string | null;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const cookies = useMemo(() => new Cookies(), []);
  const [user, setUserInfo] = useState<UserInfo | null>(null);
  const { userInfo, loading, error } = useUserInfo();

  const params = useMemo(
    () => new URLSearchParams(window.location.hash.substring(1)),
    []
  );
  const tokenParam = useMemo(() => params.get(ACCESS_TOKEN), [params]);

  useEffect(() => {
    const handleExpiredToken = () => {
      console.warn('The access token has expired. Please re-login.');
      redirectToLogin();
    };

    const processToken = (token: string) => {
      if (isTokenExpired(token)) {
        handleExpiredToken();
        return;
      }

      const paramsObject = urlParamsToJson(params);
      cookies.set(ACCESS_TOKEN, JSON.stringify(paramsObject));
      storageService.setAccessToken(token);
    };

    if (tokenParam) {
      processToken(tokenParam);
      return;
    }

    const storedAccessToken = storageService.getAccessToken();

    if (!storedAccessToken || isTokenExpired(storedAccessToken)) {
      handleExpiredToken();
    }
  }, [tokenParam, params, cookies]);

  useEffect(() => {
    if (userInfo) {
      setUserInfo(userInfo || null);
      cookies.set('user', JSON.stringify(userInfo));
      executeMixPanelEvents(userInfo);
    }
  }, [userInfo, cookies]);

  const executeMixPanelEvents = (userInfo: UserInfo) => {
    const { email, given_name, family_name } = userInfo || {};
    if (!email) return;

    const previouslyTrackedEmail = localStorage.getItem(
      'mixpanel_tracked_email'
    );

    if (email === previouslyTrackedEmail) {
      return; // Already tracked this user
    }

    // Mark this email as tracked
    localStorage.setItem('mixpanel_tracked_email', email);

    // Identify and track login
    mixPanelServiceInstance.identify(email);
    mixPanelServiceInstance.track(mixPanelEvents.LOGIN, {
      $email: email,
      $distinct_id: email,
      $first_name: given_name,
      $last_name: family_name,
    });
  };

  // Function to generate a nonce for added security

  // Main function to redirect to login
  const redirectToLogin = async () => {
    const codeVerifier = generateCodeVerifier(); 

    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const nonce = generateNonce();
    const scope = 'openid email profile console';

    // Construct the authentication URL dynamically
    const authenticationUrl =
      `${import.meta.env.VITE_AUTH_URL}/login?` +
      `client_id=${import.meta.env.VITE_AUTH_CLIENT_ID}&` +
      `response_type=code+token&` +
      `redirect_uri=${encodeURIComponent(
        import.meta.env.VITE_AUTH_REDIRECT_URL
      )}&` +
      `code_challenge=${encodeURIComponent(codeChallenge)}&` +
      `code_challenge_method=S256&` +
      `nonce=${encodeURIComponent(nonce)}&` +
      `scope=${encodeURIComponent(scope)}`;

    // Redirect to the generated authentication URL
    window.location.href = authenticationUrl;
  };

  const redirectLogout = () => {
    const logoutUrl =  `${
      import.meta.env.VITE_AUTH_URL
    }/logout?invalidate_tokens=true&post_logout_redirect_uri=${
      import.meta.env.VITE_AUTH_REDIRECT_URL
    }`;
    window.location.href = logoutUrl;
  };

  const logout = () => {
    redirectLogout();
    setUserInfo(null);
    document.cookie.split(';').forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
    cookies.remove('user');
    cookies.remove(ACCESS_TOKEN);
    localStorage.removeItem('timer');
    localStorage.removeItem(CACHE_KEY_USER_INFO);
    localStorage.removeItem(CACHE_KEY_PERMISSIONS);
    localStorage.removeItem('reviewStarted');
    localStorage.removeItem('validationStarted');
    clearAllObservationsFromLocalStorage();
  };

  const contextValue: AuthContextType = {
    user,
    logout,
    loadingUserInfo: loading,
    error,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
